import { Country } from '@cdaas/api-clients/dist/product-service-openapi';
import { createEntityAdapter, createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import { RootState } from '.';
import { productServiceCountriesClient } from '../api-client/product-service-client';
import { EntriesState, initialEntriesState, Loading } from '../utils/store';
import { resolveAxiosErrorResponse } from '../utils/errors';

type CountriesState = EntriesState<Country>;

export const initialAdminCountriesState: CountriesState = initialEntriesState;

const adminCountriesAdapter = createEntityAdapter<Country>();

export const fetchAdminCountries = createAsyncThunk('adminCountries/fetchAdminCountriesStatus', async (_, { rejectWithValue }) => {
  try {
    const response = await productServiceCountriesClient.getCountries();
    const { countries } = response.data;
    return countries;
  } catch (error) {
    return rejectWithValue(resolveAxiosErrorResponse(error)?.statusText);
  }
});

const adminCountries = createSlice({
  name: 'adminCountries',
  initialState: adminCountriesAdapter.getInitialState(initialAdminCountriesState),
  reducers: {
    addOneAdminCountry: adminCountriesAdapter.addOne,
    updateOneAdminCountry(state, action: PayloadAction<Country>) {
      adminCountriesAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
    },
    removeOneAdminCountry: adminCountriesAdapter.removeOne,
  },
  extraReducers: builder => {
    builder.addCase(fetchAdminCountries.pending, state => {
      state.loading = Loading.PENDING;
    });
    builder.addCase(fetchAdminCountries.fulfilled, (state, action) => {
      if (action.payload) {
        adminCountriesAdapter.setAll(state, action.payload);
        state.loading = Loading.SUCCEEDED;
      }
    });
    builder.addCase(fetchAdminCountries.rejected, state => {
      state.loading = Loading.FAILED;
    });
  },
});

export const { addOneAdminCountry, updateOneAdminCountry, removeOneAdminCountry } = adminCountries.actions;

export const { selectAll: selectAllCountries, selectById: selectCountryById } = adminCountriesAdapter.getSelectors(
  (state: RootState) => state.adminCountries,
);

export const selectAdminCountriesLoading = (state: RootState) => state.adminCountries.loading;

export default adminCountries.reducer;
